import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { Observable, combineLatest, filter, map, take } from 'rxjs';
import { CustomClaimService } from '../services/custom-claim.service';
import { AuthService } from '../services/auth.service';
import { GlobalRoles, MatchType } from '../models/custom-claims.model';

/**
 * Route Data Interface for Claim Requirements
 */
export interface ClaimRouteData {
  roles: GlobalRoles[];
  matchType?: MatchType;
  organizationId?: string;
  redirectTo?: string;
}

/**
 * Claim Guard
 * Protects routes requiring specific roles/permissions
 * Checks user's custom claims against route requirements
 * Waits for Firebase auth initialization before checking
 * Based on: agent-os/specs/01-user-authentication-sso/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class ClaimGuard  {
  constructor(
    private customClaimService: CustomClaimService,
    private authService: AuthService,
    private router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const claimData = route.data['claims'] as ClaimRouteData;

    if (!claimData || !claimData.roles) {
      // No claim requirements, allow access
      return new Observable(observer => {
        observer.next(true);
        observer.complete();
      });
    }

    const redirectTo = claimData.redirectTo || '/login';

    // Wait for Firebase auth to finish loading before checking claims
    return combineLatest([
      this.authService.loading$,
      this.authService.currentUser$
    ]).pipe(
      filter(([loading]) => !loading), // Wait until loading is complete
      take(1),
      map(([_, user]) => {
        // User is not authenticated
        if (!user) {
          return this.router.createUrlTree([redirectTo]);
        }

        // Check if user has admin or superadmin in global claims
        const hasAdminAccess = this.customClaimService.isAdmin();

        if (hasAdminAccess) {
          return true;
        }

        // Sign out the user and redirect to login with error
        this.authService.logout();

        // Navigate to login with access denied state
        this.router.navigate(['/login'], {
          queryParams: { error: 'access_denied' },
          state: {
            errorMessage: 'You do not have permission to access the Admin Portal. Please contact your system administrator.'
          }
        });

        return false;
      })
    );
  }

  canActivateChild(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    return this.canActivate(route);
  }
}
